import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

// Local Components
import AccessoriesAddEdit from "./AccessoriesStockAddEdit"
import { AccessoriesStockHeader } from "./AccessoriesStockHeader"

// CRUD Hooks
import {
  useAccessoriesStocks,
  useDeleteAccessoriesStock,
} from "hooks/useAccessoriesStock"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { calculateTotalPrice } from "utils/accessoryUtils"
import AccessoriesStockViewModal from "./AccessoriesStockView"
import AccessoriesUsageSummary from "./AccessoriesUsageSummary"

const AccessoriesStockList = () => {
  document.title = "Accessories Stock List"

  // -------------------- STATE --------------------
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [isView, setIsView] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [selectedViewData, setSelectedViewData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // -------------------- LIST PARAMS --------------------
  const [params, setParams] = useState({ page: 1, per_page: 10, search: "" })
  const setPage = page => setParams(prev => ({ ...prev, page }))
  const setSearch = search => setParams(prev => ({ ...prev, search, page: 1 }))

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isFetching,
  } = useAccessoriesStocks(params)
  const { mutateAsync: deleteItem, isLoading: deleteLoading } =
    useDeleteAccessoriesStock()

  // -------------------- MODAL HANDLERS --------------------
  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewData(item)
    setIsView(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Accessories stock deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete accessories stock.")
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Memo No",
        accessorKey: "memo_no",
        cell: ({ row }) => <AccessoriesUsageSummary items={row.original.items} memoNo={row.original.memo_no} />,
      },
      {
        header: "Purchase Date",
        accessorKey: "purchase_date",
        cell: ({ row }) =>
          row.original.purchase_date
            ? formatReadableDate(row.original.purchase_date)
            : "-",
      },
      {
        header: "Purchased By",
        accessorKey: "purchase_by.name",
        cell: ({ row }) => row.original.purchase_by?.name || "-",
      },
      {
        header: "Purchased From",
        accessorKey: "purchase_from.name",
        cell: ({ row }) => row.original.purchase_from?.name || "-",
      },
      {
        header: "Total Price",
        accessorKey: "items",
        cell: ({ row }) => (
          <span>{formatBDT(calculateTotalPrice(row.original.items))}</span>
        ),
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex flex-wrap gap-1">
              <Button
                color="primary"
                size="sm"
                onClick={() => handleViewClick(data)}
                id={`viewtooltip-${data.id}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${data.id}`}
              >
                View
              </UncontrolledTooltip>

              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper
      title="Accessories Stock List"
      breadcrumbItem="Accessories Stock List"
    >
      <AccessoriesStockHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={() => setParams({ page: 1, per_page: 10, search: "" })}
        isRefetching={isFetching}
        hasActiveFilters={!!params.search}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isFetching}
        className="table-responsive table-hover"
      />

      <AccessoriesAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />

      <AccessoriesStockViewModal
        isOpen={isView}
        data={selectedViewData}
        toggle={() => {
          setIsView(false)
          setSelectedViewData(null)
        }}
      />
    </PageWrapper>
  )
}

export default AccessoriesStockList
